import { Badge, Button, buttonClass, cardClass, fieldClass, textareaClass } from '@ledgerloom/ui';
import { Fragment, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type Outcome } from '../api';
import { useApi, usePageTitle } from '../hooks';
import { useSession } from '../session';
import { ErrorBanner, Icon, Loading, outcomeMessage, outcomeTone, useToast, badgeTone } from '../components/ui';
import { Field } from '../components/Field';

// The welcome interview a new workspace's owner is offered
// (docs/plans/owner-onboarding-plan.md). Start, Later, or No thanks; then short
// rounds of questions from the onboarding agent, each answered as one form;
// then the setup it drafted, to approve, and the agents it recommends, to hire.
// Patterns: advanced/wizard (one round at a time, with how far along it is),
// forms/radio and forms/text-field for the answers, ai-intelligence/ai-loading-states
// while the agent works, and data-display/card for the setup and the agents.

interface Question { id: string; text: string; choices: string[]; area: string | null; suggested: string | null; answer: string | null }
interface Round { task_id: string; thread_id: string; status: string; intro: string; asked_at: string; answer: string | null; questions: Question[] }
export type AreaState = 'signed' | 'confirmed' | 'skipped' | 'drafted' | 'answered' | 'open';
export interface AreaDraft { proposal_id: string; schema: string; approval_id: string | null; by: string | null; payload: Record<string, unknown> }
export interface SetupArea { key: string; title: string; learns: string; by: string; state: AreaState; now: Record<string, unknown>; drafts: AreaDraft[] }
interface Setup { proposal_id: string; state: string; approval_id: string | null; created_at: string; payload: SetupPayload }
interface SetupPayload {
  name?: string; currency?: string; timezone?: string; solo_signoff?: boolean; reason: string;
  profile?: { industry?: string; size_band?: string; region_band?: string; product_mix?: string };
  person?: { user_id: string; display_name?: string; title?: string; timezone?: string };
  recommended_agents?: Array<{ template_id: string; why: string }>;
  wishes?: Array<{ wish: string; build?: string; not_in_plan?: string[] }>;
}
export interface Interview {
  status: 'offered' | 'started' | 'deferred' | 'cancelled' | 'done' | null;
  person: { user_id: string; display_name: string; email: string };
  workspace: { name: string };
  interviewer: { agent_id: string; status: string } | null;
  builder: { agent_id: string | null; status: string | null } | null;
  not_in_plan: Array<{ id: string; title: string; description: string }>;
  task:{ id: string; status: string; created_at: string } | null;
  request: { mode: Rerun; area: string | null; areas: string[] } | null;
  areas: SetupArea[];
  rounds: Round[];
  setup: Setup | null;
  plan: { filename: string; created_at: string } | null;
}
type Rerun = 'continue' | 'fill_gaps' | 'revisit' | 'start_over';
type Action = 'start' | Rerun | 'later' | 'no_thanks' | 'finish';

const MAX_ROUNDS = 6;
const WORKING = ['queued', 'claimed'];
// Done is what the workspace shows set up, or the owner said is right; the rest still asks for something.
export const areaDone = (a: SetupArea) => a.state === 'signed' || a.state === 'confirmed';
export const areaPending = (a: SetupArea) => a.state === 'open' || a.state === 'answered' || a.state === 'drafted';
export const STATE: Record<AreaState, { label: string; tone: string }> = {
  signed: { label: 'Done', tone: 'ok' }, confirmed: { label: 'Done', tone: 'ok' }, skipped: { label: 'Skipped', tone: '' },
  drafted: { label: 'Ready to approve', tone: 'warn' }, answered: { label: 'Answered', tone: 'info' }, open: { label: 'Not set up', tone: '' },
};

export function Welcome() {
  const { refreshMe } = useSession();
  const iv = useApi<Interview>('/v1/onboarding');
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  usePageTitle('Welcome');

  const data = iv.data;
  // Only the latest interview asks; a round left open by one it replaced expires on its own.
  const current = data?.rounds.filter((r) => r.task_id === data.task?.id) ?? [];
  const open = current.find((r) => r.status === 'open' && !r.answer) ?? null;
  const working = data?.status === 'started' && !!data.task && (WORKING.includes(data.task.status) || (data.task.status === 'waiting' && !open));
  // While the agent works, look again every two seconds.
  const reload = iv.reload;
  useEffect(() => {
    if (!working) return;
    const t = setInterval(() => void reload(), 2000);
    return () => clearInterval(t);
  }, [working, reload]);

  async function act(action: Action) {
    setBusy(true); setError(null);
    try {
      await api('/v1/onboarding', { body: { action } });
      refreshMe();
      if (action === 'later' || action === 'no_thanks' || action === 'finish') navigate('/');
      else await reload();
    } catch (e) { setError(e); } finally { setBusy(false); }
  }

  if (iv.loading && !data) return <Loading label="Loading your welcome" />;
  if (!data) return <div className="stack"><ErrorBanner error={iv.error} /></div>;

  const first = data.person.display_name.split(' ')[0];
  const started = data.status === 'started' && !!data.task;
  const finished = started && !working && !open;

  return (
    <div className="stack welcome">
      <div className="page-head">
        <div>
          <h1>Welcome to {data.workspace.name}</h1>
          <p>{started ? 'Your onboarding agent is setting up the workspace with you.' : `Let's set up the workspace around how you work, ${first}.`}</p>
        </div>
      </div>
      <ErrorBanner error={error ?? iv.error} />

      {!data.interviewer ? (
        <section className={`${cardClass} card-pad stack`}>
          <p style={{ margin: 0 }}>This workspace has no onboarding agent. You can set it up yourself in Settings.</p>
          <div className="row"><Link className={buttonClass()} to="/admin">Open Settings</Link><Button type="button" variant="secondary" disabled={busy} onClick={() => void act('finish')}>Go to the workspace</Button></div>
        </section>
      ) : !started && data.rounds.length ? (
        <SetupAreas areas={data.areas} busy={busy} onAct={(a) => void act(a)} />
      ) : !started ? (
        <section className={`${cardClass} card-pad stack`} aria-labelledby="intro-h">
          <div className="row" style={{ gap: 12, flexWrap: 'nowrap' }}><span className="welcome-icon" aria-hidden="true"><Icon name="agent" /></span><h2 id="intro-h" style={{ margin: 0 }}>Meet your onboarding agent</h2></div>
          <p style={{ margin: 0 }}>It asks a few short rounds of questions: about the business, how it sells, and about you. Most people finish in three or four rounds. Then it drafts the workspace's setup and suggests the agents worth hiring first.</p>
          <p className="muted" style={{ margin: 0 }}>Nothing changes until you approve it. Skip any question you like, and stop whenever you want; it picks up where you left off.</p>
          <PlanUpload plan={data.plan} onAdded={() => void reload()} />
          <div className="row">
            <Button type="button" disabled={busy} onClick={() => void act('start')}>{busy ? 'Starting…' : data.status === 'deferred' || data.status === 'cancelled' ? 'Start the interview' : 'Start'}</Button>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => void act('later')}>Later</Button>
            <Button type="button" variant="ghost" disabled={busy} onClick={() => void act('no_thanks')}>No thanks</Button>
          </div>
        </section>
      ) : open ? (
        <RoundForm key={open.thread_id} round={open} n={current.indexOf(open) + 1} onAnswered={() => void reload()} onLater={() => void act('later')} />
      ) : working ? (
        <section className={`${cardClass} card-pad stack`} role="status" aria-live="polite">
          <div className="row" style={{ gap: 12 }}><div className="spinner" aria-hidden="true" /><strong>{data.rounds.length ? 'Reading your answers…' : 'Getting the first questions ready…'}</strong></div>
          <p className="muted" style={{ margin: 0 }}>This takes a few seconds.</p>
          <div className="row"><Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => void act('later')}>Finish later</Button></div>
        </section>
      ) : (
        <>
          {finished && data.setup ? (
            <SetupReview setup={data.setup} wishes={data.setup.payload.wishes?.length ? <Wishes interview={data} /> : null} onChanged={() => void reload()} onFinish={() => void act('finish')} busy={busy} />
          ) : (
            <section className={`${cardClass} card-pad stack`}>
              <p style={{ margin: 0 }}>The interview ended with nothing new to approve.</p>
              <div className="row"><Button type="button" disabled={busy} onClick={() => void act('finish')}>Go to the workspace</Button></div>
            </section>
          )}
          <SetupAreas areas={data.areas} busy={busy} onAct={(a) => void act(a)} />
        </>
      )}
    </div>
  );
}

// One round: each question with its choices and room for an answer in your own words.
function RoundForm({ round, n, onAnswered, onLater }: { round: Round; n: number; onAnswered: () => void; onLater: () => void }) {
  // An earlier answer starts filled in, for the owner to keep or change.
  const [answers, setAnswers] = useState<Record<string, string>>(() => Object.fromEntries(round.questions.filter((q) => q.suggested).map((q) => [q.id, q.suggested!])));
  const suggested = round.questions.some((q) => q.suggested);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const set = (id: string, v: string) => setAnswers((a) => ({ ...a, [id]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    // One reply: each question, then its answer; a blank one is a skip.
    const body = round.questions.map((q) => `${q.text}\n${answers[q.id]?.trim() || '(skipped)'}`).join('\n\n');
    try {
      await api('/v1/agent-threads/' + round.thread_id + '/reply', { body: { kind: 'answer', body } });
      onAnswered();
    } catch (err) { setError(err); setBusy(false); }
  }

  return (
    <form className={`${cardClass} card-pad stack`} onSubmit={submit} aria-labelledby="round-h">
      <p className="faint small" style={{ margin: 0 }}>Round {n} · at most {MAX_ROUNDS}</p>
      <h2 id="round-h" style={{ margin: 0 }}>{round.intro}</h2>
      <ErrorBanner error={error} focusOnShow />
      {round.questions.map((q) => {
        const id = `q-${q.id}`;
        const value = answers[q.id] ?? '';
        return q.choices.length ? (
          <fieldset key={q.id} className="stack" style={{ border: 0, padding: 0, margin: 0, gap: 6 }}>
            <legend style={{ fontWeight: 600, marginBottom: 6 }}>{q.text}</legend>
            {q.choices.map((c) => (
              <label key={c} className="row small" style={{ gap: 8, flexWrap: 'nowrap' }}>
                <input type="radio" name={id} value={c} checked={value === c} onChange={() => set(q.id, c)} />{c}
              </label>
            ))}
            <label className="small" htmlFor={`${id}-own`}>Or in your own words</label>
            <input id={`${id}-own`} className={fieldClass} value={q.choices.includes(value) ? '' : value} onChange={(e) => set(q.id, e.target.value)} maxLength={500} />
          </fieldset>
        ) : (
          <div key={q.id} className="field">
            <label htmlFor={id}>{q.text}</label>
            <textarea id={id} className={textareaClass} rows={2} value={value} onChange={(e) => set(q.id, e.target.value)} maxLength={1000} />
          </div>
        );
      })}
      <p className="faint small" style={{ margin: 0 }}>{suggested ? 'Your earlier answers are filled in: change any that are out of date. ' : ''}Leave any question blank to skip it.</p>
      <div className="row">
        <Button type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send answers'}</Button>
        <Button type="button" variant="ghost" disabled={busy} onClick={onLater}>Finish later</Button>
      </div>
    </form>
  );
}

// A business plan for the agent to read before it asks (forms/file-input): sent as soon as it is chosen.
function PlanUpload({ plan, onAdded }: { plan: Interview['plan']; onAdded: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  async function pick(file: File | undefined) {
    if (!file) return;
    setBusy(true); setError(undefined);
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      await api('/v1/onboarding/plan', { form });
      onAdded();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }
  return (
    <div className="stack" style={{ gap: 6 }}>
      <Field label="Business plan (optional)" error={error} hint="A PDF, Word, or text file, up to 5 MB. The agent reads it and asks you only to confirm what it says.">
        <input type="file" accept=".pdf,.docx,.txt,.md,application/pdf,text/plain" disabled={busy} onChange={(e) => void pick(e.target.files?.[0])} />
      </Field>
      <p role="status" className="small" style={{ margin: 0 }}>{busy ? 'Reading the plan…' : plan ? `Added: ${plan.filename}` : ''}</p>
    </div>
  );
}

// The workspace's setup areas, each with how far it is, and the ways to run the interview again.
function SetupAreas({ areas, busy, onAct }: { areas: SetupArea[]; busy: boolean; onAct: (a: Action) => void }) {
  return (
    <section className={`${cardClass} card-pad stack`} aria-labelledby="areas-h">
      <h2 id="areas-h" style={{ margin: 0 }}>Workspace setup: {areas.filter(areaDone).length} of {areas.length} done</h2>
      <ul className="list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {areas.map((a) => (
          <li key={a.key} className="list-item">
            <div className="grow"><div className="item-title">{a.title}</div><div className="item-sub">{a.learns}</div></div>
            <Badge tone={badgeTone(STATE[a.state].tone)}>{STATE[a.state].label}</Badge>
          </li>
        ))}
      </ul>
      <p className="muted small" style={{ margin: 0 }}>Continue asks about what is not set up yet. Fill the gaps also asks about what you skipped. Start over asks about everything again, with your earlier answers filled in. Nothing you set up is lost.</p>
      <div className="row">
        {areas.some(areaPending) && <Link className={buttonClass()} to="/welcome/setup">Walk through the setup</Link>}
        <Button type="button" variant="secondary" disabled={busy} onClick={() => onAct('continue')}>Continue</Button>
        <Button type="button" variant="secondary" disabled={busy} onClick={() => onAct('fill_gaps')}>Fill the gaps</Button>
        <Button type="button" variant="ghost" disabled={busy} onClick={() => onAct('start_over')}>Start over</Button>
      </div>
    </section>
  );
}

const SIZE: Record<string, string> = { '1': 'Just one person', '2-10': '2 to 10 people', '11-50': '11 to 50 people', '51-200': '51 to 200 people', '201-1000': '201 to 1,000 people', '1000+': 'More than 1,000 people' };

// What the interview drafted: approve it, and hire the agents it suggests.
// What the owner asked for that no agent they can hire does, each with its next
// step: the agent builder with the request written, and the library agents their
// plan leaves out. The caller gives the heading.
export function Wishes({ interview }: { interview: Interview }) {
  const title = (id: string) => interview.not_in_plan.find((t) => t.id === id)?.title ?? id.replace(/-/g, ' ');
  return (
    <ul className="list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {(interview.setup?.payload.wishes ?? []).map((w, i) => (
        <li key={i} className="list-item">
          <div className="grow">
            <div className="item-title">{w.wish}</div>
            {w.not_in_plan?.length ? <div className="item-sub">Not in your plan: {w.not_in_plan.map(title).join(', ')}. Ask Mazu to add {w.not_in_plan.length === 1 ? 'it' : 'them'}.</div> : null}
          </div>
          {w.build && interview.builder && <Link className={buttonClass({ variant: 'secondary', size: 'sm' })} to={`/agents/build?request=${encodeURIComponent(w.build)}`}>Ask the agent builder</Link>}
        </li>
      ))}
    </ul>
  );
}

function SetupReview({ setup, wishes, onChanged, onFinish, busy }: { setup: Setup; wishes: ReactNode; onChanged: () => void; onFinish: () => void; busy: boolean }) {
  const { me, refreshMe } = useSession();
  const toast = useToast();
  const templates = useApi<Array<{ id: string; title: string; principal_types: string[] }>>('/v1/agent-templates');
  const [working, setWorking] = useState<string | null>(null);
  const [hired, setHired] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const p = setup.payload;
  const facts: Array<[string, string | undefined]> = [
    ['Workspace name', p.name], ['What it sells', p.profile?.product_mix], ['Industry', p.profile?.industry],
    ['Size', p.profile?.size_band ? SIZE[p.profile.size_band] : undefined], ['Where', p.profile?.region_band], ['Currency', p.currency], ['Business time zone', p.timezone],
    ['Your name', p.person?.display_name], ['Your role', p.person?.title], ['Your time zone', p.person?.timezone],
    ['Approvals', p.solo_signoff ? 'You hire agents and change their status on your own' : undefined],
  ];
  const shown = facts.filter(([, v]) => v);

  async function approve() {
    if (!setup.approval_id) return;
    setWorking('approve'); setError(null);
    try {
      const out = await api<Outcome>(`/v1/approvals/${setup.approval_id}/decide`, { body: { decision: 'approve' } });
      toast(outcomeMessage(out, 'The workspace is set up'), outcomeTone(out));
      refreshMe();
      onChanged();
    } catch (e) { setError(e); } finally { setWorking(null); }
  }

  async function hire(templateId: string) {
    setWorking(templateId); setError(null);
    try {
      // For the company when the agent can be, else for you, as the hire dialog chooses.
      const forCompany = templates.data?.find((t) => t.id === templateId)?.principal_types.includes('tenant') ?? true;
      const out = await api<Outcome>('/v1/agents/hire', { body: { template_id: templateId, reason: 'Recommended by the onboarding interview', ...(forCompany ? { principal_type: 'tenant' } : { principal_type: 'user', principal_id: me.user_id }) } });
      setHired((h) => ({ ...h, [templateId]: out.state === 'applied' ? 'Hired, on trial' : outcomeMessage(out) }));
    } catch (e) { setError(e); } finally { setWorking(null); }
  }

  return (
    <>
      <section className={`${cardClass} card-pad stack`} aria-labelledby="setup-h">
        <h2 id="setup-h" style={{ margin: 0 }}>Here is what it learned</h2>
        <ErrorBanner error={error} />
        {shown.length ? (
          <dl className="facts">{shown.map(([k, v]) => <Fragment key={k}><dt>{k}</dt><dd>{v}</dd></Fragment>)}</dl>
        ) : <p className="muted" style={{ margin: 0 }}>Nothing to change: the interview left the workspace as it is.</p>}
        <p className="muted small" style={{ margin: 0 }}>{p.reason}</p>
        {setup.state === 'pending_approval' ? (
          <div className="row"><Button type="button" disabled={!!working} onClick={() => void approve()}>{working === 'approve' ? 'Approving…' : 'Approve this setup'}</Button></div>
        ) : (
          <p role="status" className="small" style={{ margin: 0 }}>{setup.state === 'applied' ? 'Approved: the workspace is set up.' : `This setup is ${setup.state.replace('_', ' ')}.`}</p>
        )}
      </section>
      {!!p.recommended_agents?.length && (
        <section className={`${cardClass} card-pad stack`} aria-labelledby="recommend-h">
          <h2 id="recommend-h" style={{ margin: 0 }}>Agents to hire first</h2>
          <p className="muted small" style={{ margin: 0 }}>Each starts on trial: its work is checked and logged, never saved or sent, until you make it active.</p>
          <ul className="list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {p.recommended_agents.map((a) => {
              const tpl = templates.data?.find((t) => t.id === a.template_id);
              const forYou = tpl ? !tpl.principal_types.includes('tenant') : false;
              return (
                <li key={a.template_id} className="list-item">
                  <div className="grow"><div className="item-title">{tpl?.title ?? a.template_id}</div><div className="item-sub">{a.why} {forYou ? <>Works for you: it drafts in your name, and you approve each draft. <Link to={`/agents/hire?template=${encodeURIComponent(a.template_id)}`}>Hire it for someone else</Link>.</> : 'Works for the company.'}</div></div>
                  {hired[a.template_id]
                    ? <Badge tone="success" role="status">{hired[a.template_id]}</Badge>
                    : <Button type="button" variant="secondary" size="sm" disabled={!!working} onClick={() => void hire(a.template_id)} aria-label={`Hire ${tpl?.title ?? a.template_id}${forYou ? ' for you' : ''}`}>{working === a.template_id ? 'Hiring…' : forYou ? 'Hire for me' : 'Hire'}</Button>}
                </li>
              );
            })}
          </ul>
        </section>
      )}
      {wishes && (
        <section className={`${cardClass} card-pad stack`} aria-labelledby="wishes-h">
          <h2 id="wishes-h" style={{ margin: 0 }}>You also asked for</h2>
          <p className="muted small" style={{ margin: 0 }}>No agent you can hire does these yet. Here is the next step for each.</p>
          {wishes}
        </section>
      )}
      <div className="row"><Button type="button" disabled={busy} onClick={onFinish}>Go to the workspace</Button><Link className={buttonClass({ variant: 'ghost' })} to="/desk">Open the Agent Desk</Link></div>
    </>
  );
}
